import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  MapPin, 
  Users, 
  Building2, 
  Briefcase, 
  ArrowLeft,
  Globe,
  Clock
} from "lucide-react";
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface CompanyProfile {
  id: string;
  company_name: string;
  sector: string | null;
  city: string | null;
  state: string | null;
  employee_count: string | null;
  description: string | null;
  logo_url: string | null;
  website: string | null;
}

interface Job {
  id: string;
  title: string;
  description: string;
  job_type: string[];
  city: string | null;
  state: string | null;
  salary_range_start: number | null;
  salary_range_end: number | null;
  seniority_level: string[] | null;
  created_at: string;
}

export default function EmpresaVagasPublicPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!companyId) return;

      setLoading(true);

      // Fetch company data
      const { data: companyData, error: companyError } = await supabase
        .from("company_profiles")
        .select("id, company_name, sector, city, state, employee_count, description, logo_url, website")
        .eq("id", companyId)
        .eq("is_verified", true)
        .maybeSingle();

      if (companyError || !companyData) {
        setLoading(false);
        return;
      }

      setCompany(companyData);

      // Fetch jobs (excluding confidential ones)
      const { data: jobsData } = await supabase
        .from("jobs")
        .select("id, title, description, job_type, city, state, salary_range_start, salary_range_end, seniority_level, created_at")
        .eq("company_id", companyId)
        .eq("is_active", true)
        .eq("is_confidential", false)
        .order("created_at", { ascending: false });

      setJobs(jobsData || []);
      setLoading(false);
    };

    fetchData();
  }, [companyId]);

  const formatSalary = (min: number | null, max: number | null) => {
    if (!min && !max) return "A definir";
    if (min && max) {
      return `R$ ${min.toLocaleString("pt-BR")} - R$ ${max.toLocaleString("pt-BR")}`;
    }
    if (min) return `A partir de R$ ${min.toLocaleString("pt-BR")}`;
    return `Até R$ ${max?.toLocaleString("pt-BR")}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Hoje";
    if (diffDays === 1) return "Ontem";
    if (diffDays < 7) return `${diffDays} dias atrás`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} semanas atrás`;
    return date.toLocaleDateString("pt-BR");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 container py-8">
          <Skeleton className="h-8 w-32 mb-6" />
          <div className="bg-card rounded-xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <Skeleton className="h-20 w-20 rounded-xl" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
          </div>
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 container py-8">
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Empresa não encontrada</h3>
            <p className="text-muted-foreground mb-4">A empresa que você procura não existe ou não está mais disponível.</p>
            <Button onClick={() => navigate("/empresas")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para Empresas
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Back button */}
        <div className="container pt-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/empresas")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para Empresas
          </Button>
        </div>

        {/* Company Header */}
        <section className="container py-6">
          <div className="mobile-container md:w-full">
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="flex h-20 w-20 items-center justify-center rounded-xl bg-primary/10 text-primary font-bold text-3xl shrink-0">
                  {company.company_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-2">
                    {company.company_name}
                  </h1>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                    {company.sector && (
                      <div className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        <span>{company.sector}</span>
                      </div>
                    )}
                    {(company.city || company.state) && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        <span>
                          {company.city}{company.city && company.state && ", "}{company.state}
                        </span>
                      </div>
                    )}
                    {company.employee_count && (
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{company.employee_count} funcionários</span>
                      </div>
                    )}
                    {company.website && (
                      <a 
                        href={company.website.startsWith("http") ? company.website : `https://${company.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-primary hover:underline"
                      >
                        <Globe className="h-4 w-4" />
                        <span>Website</span>
                      </a>
                    )}
                  </div>
                  {company.description && (
                    <p className="text-muted-foreground">{company.description}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        </section>

        {/* Jobs List */}
        <section className="container pb-12">
          <div className="mobile-container md:w-full">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">
              Vagas Abertas ({jobs.length})
            </h2>
          </div>

          {jobs.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl">
              <Briefcase className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Nenhuma vaga disponível</h3>
              <p className="text-muted-foreground">Esta empresa não possui vagas abertas no momento.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {jobs.map((job) => (
                <Link key={job.id} to={`/vagas/${job.id}`}>
                  <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                            {job.title}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                            {job.description}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {job.seniority_level?.map((level) => (
                              <Badge key={level} variant="outline">
                                {level}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col items-start md:items-end gap-2 text-sm">
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            <span>
                              {job.city || job.state 
                                ? `${job.city || ""}${job.city && job.state ? ", " : ""}${job.state || ""}`
                                : "Remoto"
                              }
                            </span>
                          </div>
                          <div className="font-medium text-primary">
                            {formatSalary(job.salary_range_start, job.salary_range_end)}
                          </div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            <span>{formatDate(job.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
